/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-03-03 15:06:11                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-03-03 15:10:07                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
*/

/* Imports */
import { Typography } from "@material-tailwind/react";
import { ShieldCheckIcon, ShieldExclamationIcon } from "@heroicons/react/24/outline";
import { t } from "i18next";
/***/

/* Components */
import ColorDisplay from "../color/ColorDisplay";
import UsersAdminActions from "../../../views/super-admin/views/users/UsersAdminActions";
import { toSinceString, formatRoles, formatLastDiag } from "../../../services/utils/utils";
import { OverflowTooltip } from "../../table/TableCellTooltip";
import { AutoDiagLatest } from "../../../types/auto-diag/autoDiagSubmission";
import { AutoDiagTechnicianStatus, isLateForAutoDiag } from "../../../services/auto-diag/autoDiagStatus";
/***/

/**
* TechnicianListItem: Display technician item
* @param tech - technician item to display
* @param onClick - action to do on click
* @param isLast - is last item 
*/
export default function TechnicianListItem({
  tech,
  autoDiag,
  autoDiagStatus,
  onClick,
  isLast,
  isAdminComponent,
  onChange,
}: {
  tech: EasyTech.Technician,
  /** The technician's most recent Auto Diag record; undefined when they have never submitted. */
  autoDiag?: AutoDiagLatest,
  /** The technician's Auto Diag standing; undefined when they are up to date. */
  autoDiagStatus?: AutoDiagTechnicianStatus,
  onClick?: () => void,
  isLast: boolean,
  isAdminComponent: boolean,
  onChange: () => void,
}) {
  const href = `/users/${tech.id}?userType=TECHNICIAN`;
  const rolesString = formatRoles(tech.roles);
  const lastDiag = formatLastDiag(autoDiag?.submittedAt);
  const isLate = isLateForAutoDiag(autoDiagStatus);

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    if (e.ctrlKey || e.metaKey) {
      window.open(href, "_blank");
      return;
    }
    onClick?.();
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    if (e.button === 1) {
      e.preventDefault();
      window.open(href, "_blank");
    }
  };

  return (
    <tr
      className={`page-list-item${isLast ? " last" : ""}`}
      tabIndex={0}
      style={{ cursor: "pointer" }}
      onClick={handleRowClick}
      onMouseDown={handleMouseDown}
      data-href={href}
    >
      <td>
        <OverflowTooltip content={tech.lastName || ""} className="et-cell-truncate">
          <Typography variant="small" color="blue-gray">
            {tech.lastName || "error"}
          </Typography>
        </OverflowTooltip>
      </td>
      <td>
        <OverflowTooltip content={tech.firstName || ""} className="et-cell-truncate">
          <Typography variant="small" color="blue-gray">
            {tech.firstName || "error"}
          </Typography>
        </OverflowTooltip>
      </td>
      <td>
        <OverflowTooltip content={tech.email || t("common:noEmail")} className="et-cell-truncate">
          <Typography variant="small" color="blue-gray">
            {tech.email || t("common:noEmail")}
          </Typography>
        </OverflowTooltip>
      </td>
      <td>
        <OverflowTooltip content={tech.attributes?.phone || t("common:noPhone")} className="et-cell-truncate">
          <div>
            {tech.attributes && tech.attributes.phone ? (
              <Typography variant="small" color="blue-gray">
                {tech.attributes.phone}
              </Typography>
            ) : (
              <i>{t("common:noPhone")}</i>
            )}
          </div>
        </OverflowTooltip>
      </td>
      <td>
        <div className="et-cell-wrap">
          {tech.van ? (
            <Typography variant="small" color="blue-gray">
              {tech.van.name}
            </Typography>
          ) : (
            <i>-</i>
          )}
        </div>
      </td>
      <td>
        {/*
          * The overdue marker rides in this cell rather than in a column of its own: lateness is a
          * property of the date already shown here, and a new column would be one more place for
          * TABLE_HEAD and these cells to fall out of step.
          */}
        <div className="et-cell-wrap flex items-center gap-1">
          {isLate ? (
            <ShieldExclamationIcon
              className="w-5 shrink-0 text-red-500"
              title={autoDiagStatus?.blocked
                ? t("common:autoDiag.lateBlocked")
                : t("common:autoDiag.late")}
            />
          ) : (
            autoDiag !== undefined && (
              <ShieldCheckIcon
                className="w-5 shrink-0 text-green-500"
              />
            )
          )}
          {lastDiag && (
            <Typography variant="small" color={isLate ? "red" : "blue-gray"}>
              {lastDiag}
            </Typography>
          )}
        </div>
      </td>
      <td>
        {/*
          * Nothing at all when the technician has never submitted. A red shield here would read as
          * "licence invalid", which is a different and unfair claim from "no record yet".
          */}
        {autoDiag === undefined ? (
          <Typography variant="small" color="blue-gray">—</Typography>
        ) : autoDiag.drivingLicenceValid ? (
          <ShieldCheckIcon className="w-5 text-green-500" title={t("admin:autoDiag.licenceValid")} />
        ) : (
          <ShieldExclamationIcon className="w-5 text-red-500" title={t("admin:autoDiag.licenceInvalid")} />
        )}
      </td>
      <td>
        <div className="et-cell-wrap">
          <Typography variant="small" color="blue-gray">
            {rolesString || "-"}
          </Typography>
        </div>
      </td>
      <td>
        <ColorDisplay value={tech.attributes && tech.attributes?.color} />
      </td>

      {isAdminComponent === true && (
        <>
          <td className="z-10">
            {tech.device
              ? tech.device.applicationVersionCode + " - " + tech.device.applicationVersionName
              : "-"}
          </td>
          <td className="z-10">
            {tech.location ? toSinceString(tech.location.updated) : "-"}
          </td>
          <td className="z-10" data-no-link>
            <UsersAdminActions user={tech} onChange={onChange} />
          </td>
        </>
      )}
    </tr>
  );
}
